import { useCallback, useEffect, useRef } from 'react'
import { Board } from '../lib/board'
import type { Stroke } from '../lib/types'
import { useTools } from '../store/session'

type Opts = {
  onStrokeEnd?: (s: Stroke) => void
  onChange?: (s: Stroke[]) => void
}

/** Создаёт Board на <canvas>, держит его размер и отдаёт ссылку наружу. */
export function useBoard({ onStrokeEnd, onChange }: Opts) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const boardRef = useRef<Board | null>(null)
  const cb = useRef({ onStrokeEnd, onChange })
  cb.current = { onStrokeEnd, onChange }

  const fit = useCallback(() => {
    const b = boardRef.current
    const el = canvasRef.current?.parentElement
    if (!b || !el) return
    const w = el.clientWidth
    if (!w) return
    // Нижняя граница обязательна: при нулевой высоте canvas.toDataURL() отдаёт
    // пустой «data:,» — рисунок молча уходил бы в никуда.
    const h = Math.max(240, Math.min(Math.round(w * 1.1), Math.round(window.innerHeight * 0.56)))
    if (b.canvas.width === Math.round(w * b.dpr) && b.canvas.height === Math.round(h * b.dpr)) return
    b.resize(w, h)
  }, [])

  useEffect(() => {
    const el = canvasRef.current
    if (!el) return
    const board = new Board(el, {
      getTool: () => useTools.getState(),
      onStrokeEnd: (s) => cb.current.onStrokeEnd?.(s),
      onChange: (s) => cb.current.onChange?.(s),
    })
    boardRef.current = board
    fit()
    window.addEventListener('resize', fit)
    return () => {
      window.removeEventListener('resize', fit)
      board.destroy()
      boardRef.current = null
    }
  }, [fit])

  return { canvasRef, boardRef }
}
